import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useWizard } from "../../context/WizardContext";
import { useTranslation } from "../../i18n/useTranslation";
import { useWizardStore } from "../../store/wizardStore";
import { bookLanguageDisplay } from "../../i18n/bookLanguageDisplay";
import WizardLayout from "../../components/Wizard/WizardLayout";
import AdminPageNavigator from "../../components/AdminPageNavigator";

const TOTAL_STEPS = 5;
const CURRENT_STEP = 5;

const ReviewStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { storyDetails } = useWizardStore();
  const {
    language,
    storyType,
    storyStyle,
    images,
    storyDescription,
    storyGuidelines,
    storyTitle,
    setStoryDescription,
    setStoryGuidelines,
    setStoryTitle,
    submitStoryDescription,
    isLoading,
    error,
    clearError,
  } = useWizard();

  const isProductionRegularUser = user?.productionMode === true && user?.isAdmin !== true;

  useEffect(() => {
    if (storyDetails.description.trim()) {
      setStoryDescription(storyDetails.description);
      setStoryGuidelines(storyDetails.notes);
    }
    if (typeof storyDetails.storyTitle === "string") {
      setStoryTitle(storyDetails.storyTitle);
    }
  }, [
    storyDetails.description,
    storyDetails.notes,
    storyDetails.storyTitle,
    setStoryDescription,
    setStoryGuidelines,
    setStoryTitle,
  ]);

  const handleGenerate = async () => {
    clearError();
    const ok = await submitStoryDescription();
    if (!ok) return;
    if (isProductionRegularUser) {
      navigate("/create/order-summary");
    } else {
      navigate("/create/story-studio");
    }
  };

  const langLabel = bookLanguageDisplay(language, t);
  const typeLabel =
    storyType === "surrogacy"
      ? t("wizard.storyType.surrogacyName")
      : storyType === "general"
        ? t("wizard.storyType.generalName")
        : "—";
  const styleTitleKey = `wizard.storyStyle.${storyStyle}Title`;
  const styleLabel = t(styleTitleKey);

  const descPreview = (storyDescription || storyDetails.description || "").trim();
  const notesPreview = (storyGuidelines || storyDetails.notes || "").trim();
  const titlePreview = (storyTitle || storyDetails.storyTitle || "").trim();

  return (
    <WizardLayout currentStep={CURRENT_STEP} totalSteps={TOTAL_STEPS} stageClassName="story-review-stage">
      <main id="main-content" tabIndex={-1} className="relative z-10 flex w-full flex-1 flex-col items-center justify-center outline-none">
        <div className="lang-card-root">
          <p className="lang-eyebrow">{t("wizard.step", { current: CURRENT_STEP, total: TOTAL_STEPS })}</p>
          <h1 className="lang-title">{t("wizard.review.title")}</h1>
          <AdminPageNavigator align="center" />
          <p className="lang-sub">{t("wizard.review.subtitle")}</p>

          {error && (
            <div
              role="alert"
              className="mb-4 w-full rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-red-700 shadow-soft"
            >
              {error}
            </div>
          )}

          <div className="summary-strip">
            <div className="sum-chip">
              <span className="sum-label">{t("wizard.storyType.languageLabel")}</span> {langLabel}
            </div>
            <div className="sum-chip">
              <span className="sum-label">{t("wizard.details.sumType")}</span> {typeLabel}
            </div>
            <div className="sum-chip">
              <span className="sum-label">{t("wizard.details.sumStyle")}</span> {styleLabel}
            </div>
            {images.length > 0 && (
              <div className="sum-chip">
                <span className="sum-label">{t("wizard.details.sumPhotos")}</span>
                <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                  {images.length}/10
                </span>{" "}
                {t("wizard.photos.photosLabel")}
              </div>
            )}
          </div>

          <div className="story-review-preview rounded-2xl border border-[#f5e8d3] bg-[#FFF6E9]/80 p-4 leading-relaxed text-[#2C3E50]">
            {titlePreview ? (
              <>
                <p className="mb-2 font-bold text-[#40596B]">{t("wizard.details.storyTitleLabel")}</p>
                <p className="mb-4 font-semibold text-[#2C3E50]">{titlePreview}</p>
              </>
            ) : null}
            <p className="mb-2 font-bold text-[#40596B]">{t("wizard.details.mainLabel")}</p>
            <p className="whitespace-pre-wrap">{descPreview || "—"}</p>
            {notesPreview ? (
              <>
                <p className="mb-2 mt-4 font-bold text-[#40596B]">{t("wizard.details.notesLabel")}</p>
                <p className="whitespace-pre-wrap opacity-90">{notesPreview}</p>
              </>
            ) : null}
          </div>

          <div className="wizard-nav-row mt-8">
            <button type="button" className="wizard-btn-back" onClick={() => navigate("/create/story-details")} disabled={isLoading}>
              ← {t("wizard.review.backEdit")}
            </button>
            <button type="button" className="wizard-btn-next" disabled={isLoading || descPreview.length === 0} onClick={handleGenerate}>
              {isLoading ? t("wizard.common.saving") : t("wizard.review.generateCta")}
            </button>
          </div>
        </div>
      </main>
    </WizardLayout>
  );
};

export default ReviewStep;
